import { Box, useMediaQuery } from '@mui/material'
import React, { FunctionComponent, useContext, useEffect, useState } from 'react'
import { IUserDetails } from '../../../shared/services/user-details/user-details.model'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import gaiaTheme from '../../../root-components/theme';
import { FormTextInputDisplay } from '../form-field-components/new-and-edit/form-text-input';
import { FormSelectInputDisplay } from '../form-field-components/new-and-edit/form-select-input';
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../shared/utils/subsidiary-utils';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { FormColorInputDisplay } from '../form-field-components/new-and-edit/form-color-input';
import { ShallowFormDisplay } from '../form-field-components/general/shallow-form-display';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';

export const ProfileEditForm: FunctionComponent<{
  updatedUserDetails?: IUserDetails,
  userDetails?: IUserDetails,
  submitHandler: React.Dispatch<React.SetStateAction<any>>,
  setEmailDialogOpen: React.Dispatch<React.SetStateAction<any>>,
  setPasswordDialogOpen: React.Dispatch<React.SetStateAction<any>>,
  editField: string,
  setEditField: React.Dispatch<React.SetStateAction<any>>,
  errorMessage?: string,
  errorCode?: number
}> = ({
  updatedUserDetails,
  submitHandler,
  userDetails,
  setEmailDialogOpen,
  setPasswordDialogOpen,
  editField,
  setEditField,
  errorMessage,
  errorCode
}) => {
    //Services
    const subsidiaryService = SubsidiaryService.getInstance()
    //Context
    const userDetailsContext = useContext(UserDetailsContext)
    const organisation = userDetailsContext.userDetails?.organisation
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    //Layout
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
    //UseEffect
    useEffect(() => {
      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response)
        })
        .catch(err => console.log(err))
    }, [])

    //Formik
    const profileSchema = Yup.object().shape({
      firstName: Yup.string().required('First name is required'),
      surname: Yup.string().required('Last name is required'),
      email: Yup.string().email('Email is invalid').required('Email is required'),
      avatarColor: Yup.string().required('User color is required'),
      defaultSubsidiaryId: Yup.string().required(`Default ${screenLabelsContext.getLabel('subsidiary', { plural: false })} is required`)
    })

    const formik = useFormik({
      initialValues: {
        firstName: updatedUserDetails?.firstName || '',
        surname: updatedUserDetails?.surname || '',
        email: updatedUserDetails?.email || '',
        avatarColor: updatedUserDetails?.avatarColor || '',
        defaultSubsidiaryId: updatedUserDetails?.defaultSubsidiaryId || ''
      },
      validationSchema: profileSchema,
      onSubmit: (values) => {
        setEditField('');
        submitHandler(values)
      }
    });

    const capitaliseFirstName = (): string => {
      const capitaiseFirstName = formik.values.firstName.charAt(0).toUpperCase() + formik.values.firstName.slice(1).toLowerCase()
      return capitaiseFirstName;
    };

    const capitaliseSurname = (): string => {
      const capitaliseSurname = formik.values.surname.charAt(0).toUpperCase() + formik.values.surname.slice(1).toLowerCase()
      return capitaliseSurname;
    };

    const toggleEditField = (fieldName: string) => {
      setEditField(fieldName)
      if (fieldName === 'email') {
        setEmailDialogOpen(true)
      }
      if (fieldName === 'password') {
        setPasswordDialogOpen(true)
      }
    }

    const cancelEditHandler = () => {
      setEditField('')
      if (userDetails) {
        formik.setValues(userDetails)
      }
    }

    const handleColorChange = (val: string | React.ChangeEvent<HTMLInputElement>) => {
      if (typeof val === 'string') {
        formik.setFieldValue('avatarColor', val);
      } else {
        formik.setFieldValue('avatarColor', val.target.value);
      }
    };

    return (
      <Box component='form' onSubmit={formik.handleSubmit}>
        <FormTextInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'firstName'}
          title='First Name'
          name='firstName'
          value={capitaliseFirstName()}
          touched={formik.touched.firstName}
          errors={formik.errors.firstName}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          customErrorMessage={errorCode === 2 ? errorMessage : undefined}
          isTopRow={true}
        />
        <FormTextInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'surname'}
          title='Last Name'
          name='surname'
          value={capitaliseSurname()}
          touched={formik.touched.surname}
          errors={formik.errors.surname}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          customErrorMessage={errorCode === 3 ? errorMessage : undefined}
        />
        <FormTextInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'email'}
          title='Email'
          name='email'
          allowEdit={false}
          value={formik.values.email}
          touched={formik.touched.email}
          errors={formik.errors.email}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
        />
        <ShallowFormDisplay
          name={`Password`}
          value={'.......'}
          icon={IconName.EditOutlined}
          iconButtonHandler={() => toggleEditField('password')}
          editField={editField}
          labelWidth='30%'
        />
        {((subsidiaries && subsidiaries.length > 1) || (subsidiaries && organisation?.enableSubsidiaries)) &&
          <FormSelectInputDisplay
            isEditPage={true}
            editField={editField}
            setEditField={setEditField}
            disabled={editField !== 'defaultSubsidiaryId'}
            title={`Default ${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}`}
            name='defaultSubsidiaryId'
            value={formik.values.defaultSubsidiaryId}
            touched={formik.touched.defaultSubsidiaryId}
            errors={formik.errors.defaultSubsidiaryId}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            array={getSelectableSubsidiaries(subsidiaries, formik.values.defaultSubsidiaryId).sort((a, b) => a.name.localeCompare(b.name))}
            valueKey={'id'}
            labelKey={'name'}
            customErrorMessage={errorCode === 4 ? errorMessage : ''}
          />}
        <FormColorInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'avatarColor'}
          title='User Color'
          name='avatarColor'
          value={formik.values.avatarColor}
          touched={formik.touched.avatarColor}
          errors={formik.errors.avatarColor}
          onChange={handleColorChange}
          cancelEditHandler={cancelEditHandler}
          customErrorMessage={errorCode === 1 ? errorMessage : undefined}
          isBottomRow={true}
        />
      </Box>
    )
  }